/**
 * Pure helpers behind the waste screens (QRIPO-305). They live outside the
 * components so the rules that decide what may be sent to the API, and which
 * item a scanned code means, can be unit tested without a browser.
 *
 * Quantities are stored by the API in decimal(15,3) columns, so every figure
 * here is rounded to 3 places the same way.
 */

export const MAX_QTY = 1000000;

export function round3(value) {
  return Math.round(value * 1000) / 1000;
}

export function isWhole(value) {
  return Math.abs(Number(value) - Math.round(Number(value))) < 0.0005;
}

/** Quantity that is really lost: qty minus whatever was recovered. */
export function netQty(line) {
  const qty = Number(line.qty) || 0;
  const recovered = line.direction === "out" ? Number(line.recovered_qty) || 0 : 0;
  return Math.max(round3(qty - recovered), 0);
}

/** Stock once the line is applied: an 'out' line takes it away, an 'in' line puts it back. */
export function stockAfter(line) {
  const sign = line.direction === "in" ? 1 : -1;
  return round3((Number(line.stock) || 0) + sign * netQty(line));
}

/**
 * Problem with the quantity alone, as a code the screen turns into a message:
 * "invalid" | "range" | "whole" | "" (fine).
 */
export function qtyProblem(line) {
  const qty = Number(line.qty);
  if (!Number.isFinite(qty) || qty <= 0) return "invalid";
  // 0.0004 would be stored as 0 and 0.1234 silently cut to 0.123
  if (round3(qty) <= 0 || Math.abs(round3(qty) - qty) > 1e-9) return "invalid";
  if (qty > MAX_QTY) return "range";
  if (line.integerOnly && !isWhole(qty)) return "whole";
  return "";
}

/**
 * First reason a line must not be submitted, or "" when it is good to go:
 * "invalid" | "range" | "whole" | "type" | "recovered" | "stock".
 */
export function lineProblem(line) {
  const qty = qtyProblem(line);
  if (qty) return qty;

  if (!line.waste_type) return "type";

  if (line.direction === "out") {
    const recovered = Number(line.recovered_qty) || 0;
    if (recovered < 0 || recovered >= Number(line.qty)) return "recovered";
    if (line.integerOnly && !isWhole(recovered)) return "whole";
  }

  if (stockAfter(line) < 0) return "stock";
  return "";
}

/** Product code, plus the variant SKU when it differs ("TSH-01 · TSH-01-M"). */
export function codeLabel(row) {
  return [row.code, row.sku]
    .filter((value, index, all) => value && all.indexOf(value) === index)
    .join(" · ");
}

/**
 * What a scanned / typed code means for the picker list.
 *   { kind: "one", row }  exactly one item has this code, SKU or barcode
 *   { kind: "many" }      several do (a variant parent's code is shared by its variants)
 *   { kind: "none" }      nothing matches exactly
 * Only an exact match counts: a name or a partial code must never add an item.
 */
export function matchScan(rows, term) {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return { kind: "none" };

  const same = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim().toLowerCase() === needle;
  const matches = (rows || []).filter(
    (row) => same(row.code) || same(row.sku) || same(row.barcode)
  );

  if (matches.length === 1) return { kind: "one", row: matches[0] };
  if (matches.length > 1) return { kind: "many" };
  return { kind: "none" };
}

/**
 * Tells a barcode scanner from a person typing. A scanner "types" the whole code
 * within a few milliseconds per key and finishes with Enter; a person never does.
 * That lets a scan be recognised even when focus sits in some other field, so
 * the digits can be taken back out of that field instead of corrupting it.
 *
 *   const detector = createScanDetector();
 *   const hit = detector.key({ key, ctrlKey, metaKey, altKey }, performance.now(), () => snapshot);
 *   if (hit) { hit.code; hit.snapshot }
 *
 * `takeSnapshot` runs when a new burst starts, so the caller can remember what
 * the focused field contained before the first digit landed in it.
 */
export function createScanDetector({ maxGapMs = 75, burstGapMs = 150, minLength = 4 } = {}) {
  let buffer = "";
  let fast = 0;
  let last = 0;
  let snapshot = null;

  const reset = () => {
    buffer = "";
    fast = 0;
    last = 0;
    snapshot = null;
  };

  return {
    key(event, now, takeSnapshot) {
      const key = event.key;

      if (key === "Enter") {
        const isScan = buffer.length >= minLength && fast >= buffer.length - 1;
        const hit = isScan ? { code: buffer, snapshot } : null;
        reset();
        return hit;
      }

      // Shift / Control / ... on their own are not part of the code
      if (!key || key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
        if (key === "Backspace" || key === "Delete" || key === "Escape" || key === "Tab") reset();
        return null;
      }

      if (!buffer || now - last > burstGapMs) {
        buffer = key;
        fast = 0;
        snapshot = takeSnapshot ? takeSnapshot() : null;
      } else {
        if (now - last <= maxGapMs) fast += 1;
        buffer += key;
      }
      last = now;
      return null;
    },
  };
}
